import {
	ALL_FILTER_IDS,
	RECIPE_ALLERGIES_FILTER_ID,
	RECIPE_CALORIES_FILTER_ID,
	RECIPE_DIET_FORM_FILTER_ID,
	RECIPE_DURATION_FILTER_ID,
	RECIPE_MENU_TYPE_FORM_FILTER_ID,
	RECIPE_TOGO_FILTER_ID,
	WORKOUT_DURATION_FILTER_ID,
	WORKOUT_INTENSITY_FILTER_ID,
	WORKOUT_TRAINING_GOAL_FILTER_ID,
} from 'components/molecules/TagFilter/factories/index';

import { POOL_TYPE } from 'defs/pools';
import ClientStorage, { StorageTypes } from 'lib/ClientStorage';
import * as profileAT from 'store/profile/profile.actionTypes';
import { PoolEnumType } from 'types/services/magnolia/enums/PoolEnum';
import { PoolsActionType } from '../pools/pools.actionCreators';
import { SideEffectsActionTypes, TransientPoolsActionType } from './transientPoolState.actionCreators';
import {
	REQUEST_ADD_PERSISTENT_FILTER,
	REQUEST_REMOVE_PERSISTENT_FILTER,
	REQUEST_SELECTED_ITEM_SUCCESS,
	REQUEST_SET_DAY_REF,
	REQUEST_SET_OVERLAY_REF,
	REQUEST_SET_PERSISTENT_FILTER,
	REQUEST_SET_PROGRAM_ITEM_REF,
	REQUEST_UNSET_ALL_FILTERS,
	REQUEST_UNSET_CURRENT_OVERLAY_REF,
	REQUEST_UNSET_DAY_REF,
	REQUEST_UNSET_OVERLAY_REF,
	REQUEST_UNSET_PERSISTENT_FILTER,
	REQUEST_UNSET_PROGRAM_ITEM_REF,
} from './transientPoolState.actionTypes';

export type TransientPoolStateType = {
	programItemRef?: string | number;
	programItemExternalRef?: string;
	dayRef?: number;
	activeOverlayRef?: string;
	contents: {
		byId: {
			[hash: string]: Record<string, any>; // content-type map
		};
	};
	filters: {
		[key in ALL_FILTER_IDS]?: string[];
	};
};

export type RootStateType = {
	transientPoolState: TransientPoolStateType;
};

export const dedicatedPoolFilterMap: {} = {
	[POOL_TYPE.WORKOUT]: [WORKOUT_DURATION_FILTER_ID, WORKOUT_INTENSITY_FILTER_ID, WORKOUT_TRAINING_GOAL_FILTER_ID],
	[POOL_TYPE.RECIPE]: [
		RECIPE_ALLERGIES_FILTER_ID,
		RECIPE_CALORIES_FILTER_ID,
		RECIPE_DIET_FORM_FILTER_ID,
		RECIPE_DURATION_FILTER_ID,
		RECIPE_MENU_TYPE_FORM_FILTER_ID,
		RECIPE_TOGO_FILTER_ID,
	],
};

const rehydratePartialFiltersFromSession = (): { [id: string]: string[] } => {
	const SessionStorage = new ClientStorage(StorageTypes.SESSION);
	const partialState = {
		...(SessionStorage.get('pool-recipe-all') || {}),
		...(SessionStorage.get('pool-workout-all') || {}),
	};

	return Object.keys(partialState).reduce((prev, curr) => {
		if (!partialState[curr].includes('NONE')) {
			prev[curr] = partialState[curr];
		}

		return prev;
	}, {});
};

export const initialState: TransientPoolStateType = {
	programItemRef: undefined,
	dayRef: undefined,
	activeOverlayRef: undefined,
	contents: {
		byId: {},
	},
	filters: rehydratePartialFiltersFromSession(),
};

export default (
	state: TransientPoolStateType = initialState,
	action: PoolsActionType | TransientPoolsActionType | SideEffectsActionTypes
): TransientPoolStateType => {
	switch (action.type) {
		// update user data in the pool
		case profileAT.REQUEST_USER_DATA_SUCCESS:
		case profileAT.REQUEST_SAVE_DIETARY_HABITS_SUCCESS:
			return {
				...state,
				filters: {
					...state.filters,
					RECIPE_DIET_FORM: [action.data.nutritionPhilosophy],
					RECIPE_ALLERGY: action.data.allergies,
				},
			};
		case REQUEST_SET_PROGRAM_ITEM_REF:
			return {
				...state,
				programItemRef: action.programItemRef,
				programItemExternalRef: action.programItemExternalRef,
			};
		case REQUEST_UNSET_PROGRAM_ITEM_REF:
			return {
				...state,
				programItemRef: undefined,
				programItemExternalRef: undefined,
			};
		case REQUEST_SET_OVERLAY_REF:
			if (state.activeOverlayRef) {
				return state;
			}

			return {
				...state,
				activeOverlayRef: action.overlayRef,
			};
		case REQUEST_UNSET_CURRENT_OVERLAY_REF:
			return {
				...state,
				activeOverlayRef: undefined,
			};
		case REQUEST_UNSET_OVERLAY_REF:
			if (state.activeOverlayRef !== action.overlayRef) {
				return state;
			}

			return {
				...state,
				activeOverlayRef: undefined,
			};
		case REQUEST_ADD_PERSISTENT_FILTER:
			// remove NONE, UNKNOWN, MEAT_FISH filters when adding a filter as they are the filter for the 'empty' state
			return {
				...state,
				filters: {
					...state.filters,
					[action.filterId]: state.filters[action.filterId]
						? [
								...state.filters[action.filterId].filter(
									(existingFilter: string) =>
										existingFilter !== 'UNKNOWN' &&
										existingFilter !== 'NONE' &&
										existingFilter !== 'MEAT_FISH'
								),
								action.filterFactoryKey,
						  ]
						: [action.filterFactoryKey],
				},
			};
		case REQUEST_REMOVE_PERSISTENT_FILTER:
			// filter out the requested filter from the array
			const newRemovePersistentFilterValue = state.filters[action.filterId].filter(
				(filterKey: string) => filterKey !== action.filterFactoryKey
			);
			// delete filter completely if it's empty
			if (newRemovePersistentFilterValue.length === 0) {
				const filterCopy = { ...state.filters };
				delete filterCopy[action.filterId];
				return {
					...state,
					filters: {
						...filterCopy,
					},
				};
			}
			return {
				...state,
				filters: {
					...state.filters,
					[action.filterId]: newRemovePersistentFilterValue,
				},
			};
		case REQUEST_SET_PERSISTENT_FILTER:
			return {
				...state,
				filters: {
					...state.filters,
					[action.filterId]: [action.filterFactoryKey],
				},
			};
		case REQUEST_UNSET_PERSISTENT_FILTER:
			const filters = { ...state.filters };
			delete filters[action.filterId];

			return {
				...state,
				filters,
			};
		case REQUEST_SELECTED_ITEM_SUCCESS:
			return {
				...state,
				contents: {
					...state.contents,
					byId: {
						...state.contents.byId,
						[action.id]: action.content,
					},
				},
			};
		case REQUEST_UNSET_ALL_FILTERS:
			return {
				...state,
				filters: {},
			};
		case REQUEST_SET_DAY_REF:
			return {
				...state,
				dayRef: action.day,
			};
		case REQUEST_UNSET_DAY_REF:
			const updatedState = { ...state };
			delete updatedState.dayRef;

			return updatedState;
		default:
			return state;
	}
};

export const getProgramItemRef = (state: RootStateType): string | number | undefined => {
	return state.transientPoolState.programItemRef;
};

export const getProgramItemExternalRef = (state: RootStateType): string | undefined => {
	return state.transientPoolState.programItemExternalRef;
};

export const getActiveOverlayRef = (state: RootStateType): string | undefined => {
	return state.transientPoolState.activeOverlayRef;
};

export const getFilters = (state: RootStateType, selectedPool?: PoolEnumType): { [key: string]: string } => {
	const filters = state.transientPoolState.filters;

	if (selectedPool) {
		const dedicatedFilterIds = dedicatedPoolFilterMap[selectedPool];

		return Object.keys(filters).reduce(
			(dedicatedFilterFactory: { [key: string]: string }, currentFilterId: string) => {
				if (Boolean(~dedicatedFilterIds.indexOf(currentFilterId))) {
					dedicatedFilterFactory[currentFilterId] = filters[currentFilterId];
				}

				return dedicatedFilterFactory;
			},
			{}
		);
	}

	return {};
};

export const getSelectedItemById = (state: RootStateType, id: string): Record<string, any> | void => {
	return state.transientPoolState.contents.byId[id];
};

export const getDayRef = (state: RootStateType): number | void => {
	return state.transientPoolState.dayRef;
};
